import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import ExaminationWizard from './ExaminationWizard';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Input } from './ui/input';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Sparkles,
  FileText,
  User,
  Target,
  ChevronRight,
  Trash2,
  Download,
  Loader2,
  MessageCircle,
  Gavel,
  BookOpen,
  Scale,
  MoreVertical,
  ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXAMINATION_TYPES = [
  { value: 'direct_examination', label: 'Direct Examination', icon: MessageCircle },
  { value: 'cross_examination', label: 'Cross-Examination', icon: Gavel },
  { value: 'deposition', label: 'Deposition', icon: BookOpen },
  { value: 'hearing', label: 'Hearing', icon: Scale },
  { value: 'redirect', label: 'Redirect', icon: MessageCircle },
  { value: 'recross', label: 'Recross', icon: Gavel }
];

export default function CaseExaminationTab({ caseId, caseName }) {
  const { token } = useAuth();
  const [outlines, setOutlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [selectedOutline, setSelectedOutline] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [outlineToDelete, setOutlineToDelete] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchOutlines();
  }, [caseId, token]);

  const fetchOutlines = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const res = await fetch(`${API_URL}/api/examination/outlines?case_id=${caseId}`, { headers });
      if (res.ok) {
        const all = await res.json();
        // Filter to only outlines for this case
        setOutlines(all.filter(o => o.case_id === caseId));
      }
    } catch (error) {
      console.error('Error fetching outlines:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOutline = async () => {
    if (!outlineToDelete) return;
    try {
      const res = await fetch(`${API_URL}/api/examination/outlines/${outlineToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setOutlines(outlines.filter(o => o.id !== outlineToDelete.id));
        if (selectedOutline?.id === outlineToDelete.id) setSelectedOutline(null);
        toast.success('Outline deleted');
      }
    } catch {
      toast.error('Failed to delete outline');
    } finally {
      setShowDeleteDialog(false);
      setOutlineToDelete(null);
    }
  };

  const handleGenerateAI = async (sectionType) => {
    if (!selectedOutline) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/examination/outlines/${selectedOutline.id}/generate?section_type=${sectionType}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const result = await res.json();
        setSelectedOutline(result.outline);
        setOutlines(outlines.map(o => o.id === result.outline.id ? result.outline : o));
        toast.success('AI generation complete');
      }
    } catch {
      toast.error('AI generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async (format) => {
    if (!selectedOutline) return;
    try {
      const res = await fetch(`${API_URL}/api/examination/outlines/${selectedOutline.id}/export?format=${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedOutline.title}.${format}`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Exported successfully');
      }
    } catch {
      toast.error('Export failed');
    }
  };

  const filteredOutlines = outlines.filter(o =>
    o.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.witness_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTypeInfo = (type) => EXAMINATION_TYPES.find(t => t.value === type) || EXAMINATION_TYPES[0];

  if (showWizard) {
    return (
      <ExaminationWizard
        onComplete={() => { setShowWizard(false); fetchOutlines(); }}
        onCancel={() => setShowWizard(false)}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="case-examination-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-heading font-semibold">Examination Outlines</h3>
          <p className="text-sm text-muted-foreground">AI-generated witness examination outlines for this case</p>
        </div>
        <Button onClick={() => setShowWizard(true)} data-testid="create-examination-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Examination
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : outlines.length === 0 && !selectedOutline ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MessageCircle className="w-10 h-10 text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">No examination outlines yet</p>
            <Button className="mt-4" variant="outline" onClick={() => setShowWizard(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create First Outline
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Outline List */}
          <div className="lg:col-span-1 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search outlines..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <ScrollArea className="h-[500px]">
              <div className="space-y-2 pr-2">
                {filteredOutlines.map((outline) => {
                  const typeInfo = getTypeInfo(outline.examination_type);
                  return (
                    <Card
                      key={outline.id}
                      className={`cursor-pointer transition-colors hover:border-navy/30 ${selectedOutline?.id === outline.id ? 'border-navy bg-navy/5' : ''}`}
                      onClick={() => setSelectedOutline(outline)}
                      data-testid={`outline-${outline.id}`}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{outline.title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {typeInfo.label}
                              </Badge>
                              {outline.witness_name && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {outline.witness_name}
                                </span>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost" size="icon" className="h-7 w-7 shrink-0"
                            onClick={(e) => { e.stopPropagation(); setOutlineToDelete(outline); setShowDeleteDialog(true); }}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          {/* Outline Detail */}
          <div className="lg:col-span-2">
            {selectedOutline ? (
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-heading font-semibold">{selectedOutline.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge>{getTypeInfo(selectedOutline.examination_type).label}</Badge>
                        {selectedOutline.witness_name && (
                          <span className="text-sm text-muted-foreground">
                            Witness: {selectedOutline.witness_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleExport('pdf')}>
                        <Download className="w-4 h-4 mr-1" /> PDF
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleExport('docx')}>
                        <Download className="w-4 h-4 mr-1" /> DOCX
                      </Button>
                    </div>
                  </div>

                  {/* Objectives */}
                  {selectedOutline.objectives && selectedOutline.objectives.length > 0 && (
                    <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Objectives</p>
                      <ul className="text-sm space-y-1">
                        {selectedOutline.objectives.map((obj, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Target className="w-3.5 h-3.5 mt-0.5 text-navy" />
                            {obj}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Sections */}
                  <Accordion type="multiple" className="space-y-2">
                    {(selectedOutline.sections || []).map((section, idx) => (
                      <AccordionItem key={idx} value={`section-${idx}`} className="border rounded-lg px-3">
                        <AccordionTrigger className="text-sm font-medium hover:no-underline py-3">
                          <div className="flex items-center gap-2">
                            <span>{section.title || `Section ${idx + 1}`}</span>
                            {section.questions?.length > 0 && (
                              <Badge variant="secondary" className="text-xs">{section.questions.length} questions</Badge>
                            )}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-3 pb-2">
                            {(section.questions || []).map((q, qi) => (
                              <div key={qi} className="pl-4 border-l-2 border-navy/20 py-1">
                                <p className="text-sm">{q.text || q}</p>
                                {q.purpose && <p className="text-xs text-muted-foreground mt-1">Purpose: {q.purpose}</p>}
                                {q.follow_up && <p className="text-xs text-blue-600 mt-1">Follow-up: {q.follow_up}</p>}
                              </div>
                            ))}
                            <Button
                              variant="outline" size="sm" className="w-full"
                              onClick={() => handleGenerateAI(section.type || 'general')}
                              disabled={generating}
                            >
                              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                              Generate AI Questions
                            </Button>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="flex items-center justify-center py-20">
                  <p className="text-sm text-muted-foreground">Select an outline to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Delete dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Outline</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{outlineToDelete?.title}"? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteOutline} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
